import {useEffect, useRef} from 'react'
import {useSelector} from 'react-redux'
import {usePageProps} from 'context/pageProps'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getPreheatRoomsParams} from 'modules/searchApi/selectors'
import {PageProps as AccommodationPageProps} from 'pages/Hotel/[hotelSlug]/[locale]'
import {getPreheatParamsMismatchKeys} from 'utils/getPreheatParamsMismatchKeys'

import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {selectIsLoading} from '@vio/core/src/auth/modules/selectors'

/**
 * Compares the preheat params the server built with the ones the client builds once auth
 * has resolved, and reports the keys that differ. It doesn't change any behavior.
 *
 * Each distinct set of keys is reported once per page view, because some params (like
 * `isAnchor`) only settle after the first render and would be missed by a single check.
 *
 * Temporary: remove once the mismatch rate has settled near zero.
 */
export const useRoomsPreheatCanary = () => {
  const {serverPreheatRoomsParams} = usePageProps() as AccommodationPageProps
  const hotelId = useSelector(getAccommodationPageHotelId)
  const clientParams = useSelector(state =>
    getPreheatRoomsParams(state, hotelId)
  )
  const isAuthLoading = useSelector(selectIsLoading)
  const dispatchTeamEvent = useDispatchTeamEvent()
  const reportedKeySets = useRef(new Set<string>())

  useEffect(() => {
    if (!serverPreheatRoomsParams || isAuthLoading) return

    const mismatchKeys = getPreheatParamsMismatchKeys(
      serverPreheatRoomsParams,
      clientParams
    ).join(',')
    if (!mismatchKeys || reportedKeySets.current.has(mismatchKeys)) return
    reportedKeySets.current.add(mismatchKeys)

    dispatchTeamEvent(
      trackEvent({
        category: Category.System,
        entity: Entity.RoomsPreheatParams,
        action: Action.Errored,
        page: Page.Accommodation,
        meta: {mismatchKeys}
      })
    )
  }, [serverPreheatRoomsParams, clientParams, isAuthLoading, dispatchTeamEvent])
}
